/**
 * useFriendship Hook
 * Manages friendship status and actions with a specific user.
 * Shared between web and mobile.
 */

import { useCallback, useState } from 'react';
import type { HttpClient } from '../api/httpClient';
import type { FriendshipStatus, FriendshipStatusResponse } from '../friendTypes';

// =============================================================================
// Types
// =============================================================================

export interface UseFriendshipOptions {
  httpClient: HttpClient;
  targetUserId: string;
  isAuthenticated: boolean;
}

export interface UseFriendshipResult {
  status: FriendshipStatus | null;
  requestId: number | null;
  friendSince: string | null;
  loading: boolean;
  error: string | null;
  sendRequest: () => Promise<{ autoAccepted?: boolean }>;
  acceptRequest: () => Promise<void>;
  rejectRequest: () => Promise<void>;
  cancelRequest: () => Promise<void>;
  unfriend: () => Promise<void>;
  checkStatus: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

export function useFriendship(options: UseFriendshipOptions): UseFriendshipResult {
  const { httpClient, targetUserId, isAuthenticated } = options;

  const [status, setStatus] = useState<FriendshipStatus | null>(null);
  const [requestId, setRequestId] = useState<number | null>(null);
  const [friendSince, setFriendSince] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setStatus('none');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await httpClient.get<FriendshipStatusResponse>(
        `/api/friends/status/${targetUserId}`,
      );
      setStatus(data.status);
      setRequestId(data.requestId ?? null);
      setFriendSince(data.friendSince ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check friendship status');
    } finally {
      setLoading(false);
    }
  }, [httpClient, isAuthenticated, targetUserId]);

  const sendRequest = useCallback(async () => {
    if (!isAuthenticated) {
      setError('You must be logged in');
      return {};
    }

    setLoading(true);
    setError(null);

    try {
      const data = await httpClient.post<{
        success: boolean;
        autoAccepted: boolean;
        requestId?: number;
      }>(`/api/friends/request/${targetUserId}`);

      if (data.autoAccepted) {
        setStatus('friends');
        setRequestId(null);
      } else {
        setStatus('pending_sent');
        setRequestId(data.requestId ?? null);
      }

      return { autoAccepted: data.autoAccepted };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send friend request');
      return {};
    } finally {
      setLoading(false);
    }
  }, [httpClient, isAuthenticated, targetUserId]);

  const acceptRequest = useCallback(async () => {
    if (!requestId) return;

    setLoading(true);
    setError(null);

    try {
      await httpClient.post(`/api/friends/request/${requestId}/accept`);
      setStatus('friends');
      setRequestId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept friend request');
    } finally {
      setLoading(false);
    }
  }, [httpClient, requestId]);

  const rejectRequest = useCallback(async () => {
    if (!requestId) return;

    setLoading(true);
    setError(null);

    try {
      await httpClient.post(`/api/friends/request/${requestId}/reject`);
      setStatus('none');
      setRequestId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject friend request');
    } finally {
      setLoading(false);
    }
  }, [httpClient, requestId]);

  const cancelRequest = useCallback(async () => {
    if (!requestId) return;

    setLoading(true);
    setError(null);

    try {
      await httpClient.delete(`/api/friends/request/${requestId}`);
      setStatus('none');
      setRequestId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel friend request');
    } finally {
      setLoading(false);
    }
  }, [httpClient, requestId]);

  const unfriend = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await httpClient.delete(`/api/friends/${targetUserId}`);
      setStatus('none');
      setFriendSince(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unfriend user');
    } finally {
      setLoading(false);
    }
  }, [httpClient, targetUserId]);

  return {
    status,
    requestId,
    friendSince,
    loading,
    error,
    sendRequest,
    acceptRequest,
    rejectRequest,
    cancelRequest,
    unfriend,
    checkStatus,
  };
}
